import React, { useState, useEffect, useRef } from 'react';
import { styled } from '@mui/material/styles';
import MuiSelect from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Menu from '@mui/material/Menu';

const StyledSelect = styled(MuiSelect)(
  ({ theme }) => `
  min-width: 130px;
  
  & .MuiSelect-select {
    padding: 0.5rem 1.5rem;
  }
  
  & .MuiOutlinedInput-notchedOutline {
    border-radius: 8px;
  }
`
);

export const StyledMenuItem = styled(MenuItem)(
  ({ theme }) => `
  padding: 0.5rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  white-space: normal;
  word-wrap: break-word;
  overflow-wrap: break-word;

  &.Mui-selected {
    background-image: ${theme.palette.gradientColor.short};
    color: ${theme.palette.common.white};
  }

  &.Mui-focusVisible {
    background-color: ${
      theme.palette.mode === 'dark'
        ? theme.palette.grey[800]
        : theme.palette.grey[100]
    };
  }

  &.Mui-selected.Mui-focusVisible {
    background-image: ${theme.palette.gradientColor.short};
  }

  &:hover {
    background-color: ${
      theme.palette.mode === 'dark'
        ? theme.palette.grey[600]
        : theme.palette.grey[200]
    };
    color: ${
      theme.palette.mode === 'dark'
        ? theme.palette.grey[100]
        : theme.palette.grey[900]
    };
  }
  
  &.Mui-selected:hover {
    background-image: ${theme.palette.gradientColor.short};
    color: ${theme.palette.common.white};
  }
  `
);

export interface ButtonSelectProps<TValue extends {}> {
  value?: TValue;
  defaultValue?: TValue;
  onChange?: (value: TValue | null) => void;
  options: { value: TValue; text: React.ReactNode }[];
  /** use `onClickItem`  instead `onChange` when you want show the select item, but you want to chain flow when select the same item 2 or more times*/
  onClickItem?: (value: TValue) => void;
  startListItem?: React.ReactNode;
  /** Custom component to render as the trigger */
  customTrigger?:
    | React.ComponentType<any>
    | ((props: any) => React.ReactElement);
  /** Custom width for the listbox */
  listboxWidth?: number;
  [keyOtherComponents: string]: any;
}

export default function ButtonSelect<TValue extends {}>(
  props: ButtonSelectProps<TValue>
) {
  const {
    value,
    defaultValue,
    onChange,
    options,
    onClickItem,
    startListItem,
    customTrigger,
    listboxWidth,
    slots,
    ...otherProps
  } = props;

  const [internalValue, setInternalValue] = useState(
    value !== undefined ? value : defaultValue
  );

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const triggerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (value !== undefined) {
      setInternalValue(value);
    }
  }, [value]);

  const handleChange = (event: any) => {
    const newValue = event.target.value;
    setInternalValue(newValue);

    if (onChange) {
      onChange(newValue);
    }

    if (onClickItem) {
      onClickItem(newValue);
    }
  };

  const handleMenuItemClick = (optionValue: TValue) => {
    setInternalValue(optionValue);
    setAnchorEl(null);

    if (onChange) {
      onChange(optionValue);
    }

    if (onClickItem) {
      onClickItem(optionValue);
    }
  };

  const handleTriggerClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Check if slots.root is provided (for custom button components like ZoneButton)
  const trigger = customTrigger || slots?.root;
  const selectedOption = options.find((opt) => opt.value === internalValue);
  const displayValue = selectedOption?.text ?? '';

  const selectProps: any = {
    value: internalValue ?? '',
    onChange: handleChange,
    displayEmpty: true,
    MenuProps: {
      PaperProps: {
        sx: {
          ...(listboxWidth && { width: listboxWidth, maxWidth: listboxWidth }),
        },
      },
    },
    ...otherProps,
  };

  // If customTrigger or slots.root is provided, use Menu instead of Select
  if (trigger) {
    const TriggerComponent = trigger;
    const menuWidth = listboxWidth || anchorEl?.offsetWidth || 'auto';

    return (
      <>
        <TriggerComponent
          ref={triggerRef}
          onClick={handleTriggerClick}
          {...otherProps}
        >
          {displayValue}
        </TriggerComponent>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          PaperProps={{
            sx: {
              width: menuWidth,
              minWidth: menuWidth,
            },
          }}
        >
          {startListItem}
          {options.map((option) => (
            <StyledMenuItem
              key={JSON.stringify(option.value)}
              onClick={() => handleMenuItemClick(option.value)}
              selected={option.value === internalValue}
            >
              {option.text}
            </StyledMenuItem>
          ))}
        </Menu>
      </>
    );
  }

  // Default button trigger
  return (
    <StyledSelect {...selectProps}>
      {startListItem}
      {options.map((option) => (
        <StyledMenuItem
          key={JSON.stringify(option.value)}
          value={option.value as any}
        >
          {option.text}
        </StyledMenuItem>
      ))}
    </StyledSelect>
  );
}
